import { addDays, addHours, differenceInDays, format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import { EmptyState } from '../../../../components/common/empty/EmptyState';
import { AnimateLoading } from '../../../../components/common/loading/AnimateLoading';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { ToastViewer } from '../../../../components/common/text-viewer/ToastViewer';
import { flarelaneEventKeys } from '../../../../constants/flarelaneEvent/flarelaneEvent.contant';
import { OpenMarketProductApprovalStatusConstant } from '../../../../constants/openMarketProduct/openMarketProduct';
import { pageInfo, sellTractorPageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { ProductStatusConstant } from '../../../../constants/product/product.constant';
import { usePostBumpUserSellTractorMutation } from '../../../../hooks/api/product/mutations/usePostBumpUserSellTractor.mutation';
import { useGetTodaySellTractorBumpStatus } from '../../../../hooks/api/product/queries/useGetTodaySellTractorBumpStatus.query';
import { usePatchUserSellTractorsMutation } from '../../../../hooks/api/user/mutations/usePatchUserSellTractors.mutation';
import { useGetUserSellTractors } from '../../../../hooks/api/user/queries/useGetUserSellTractors';
import { useAppDispatch } from '../../../../hooks/store/useStore';
import { MachineType } from '../../../../interfaces/Brand.interfaces';
import { IOpenMarketProduct, IOpenMarketProductImage } from '../../../../interfaces/OpenMarketProduct.interfaces';
import { useNotification } from '../../../../provider/ToastNotificationProvider';
import {
  imageTypes,
  tractorImageObject,
  tractorImageType,
  userSellTractorSurveySlice,
} from '../../../../store/userSellTractorSurveySlice';
import { postMessageToRN } from '../../../../utils/RNCommunicator/postMessageToRN.util';
import {
  IUserSellProductForDetailCard,
  UserSellProductDetailCard,
} from '../../../../views/Product/listView/UserSellProductDetailCard/UserSellProductDetailCard';

export const SellMachineCardGroup = ({ type }: { type: MachineType | 'ALL' }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userSellTractorActions = userSellTractorSurveySlice.actions;

  const { data: todaySellTractorBumpStatus, isLoading: isGetTodaySellTractorBumpStatusLoading } =
    useGetTodaySellTractorBumpStatus();

  const { mutate: postBumpUserSellTractor, isLoading: isPostBumpUserSellTractorLoading } =
    usePostBumpUserSellTractorMutation();

  const { openNotification } = useNotification();

  const { APPROVAL, ONGOING } = OpenMarketProductApprovalStatusConstant;
  const { ONSALE, SOLDOUT } = ProductStatusConstant;

  const [isBumpModalVisible, setIsBumpModalVisible] = useState<boolean>(false);
  const [isNewProductCannotBumpModalVisible, setIsNewProductCannotBumpModalVisible] = useState<boolean>(false);
  const [isBumpDailyLimitOverModalVisible, setIsBumpDailyLimitOverModalVisible] = useState<boolean>(false);
  const [isBumpLimitPerTractorOverModalVisible, setIsBumpLimitPerTractorOverModalVisible] = useState<boolean>(false);
  const [isCanBumpAgainTimeModalVisible, setIsCanBumpAgainTimeModalVisible] = useState<boolean>(false);
  const [isShowEditModal, setIsShowEditModal] = useState(false);
  const [isMarkAsSoldoutModalVisible, setIsMarkAsSoldoutModalVisible] = useState(false);
  const [isRejectedReasonModalVisible, setIsRejectedReasonModalVisible] = useState(false);
  const [targetSellTractorId, setTargetSellTractorId] = useState<string>('');
  const [targetSellTractor, setTargetSellTractor] = useState<IOpenMarketProduct>();
  const [rejectedReason, setRejectedReason] = useState<string>();

  const { mutate: patchUserSellTractor, isLoading: isPatchUserSellTractorLoading } = usePatchUserSellTractorsMutation();

  const {
    data: usersSellTractors,
    isLoading: isGetUsersSellTractorsLoading,
    refetch: refetchUsersSellTractors,
  } = useGetUserSellTractors({
    searchParam: {
      machineType: type === 'ALL' ? '' : type,
    },
  });

  const sellTractorStatusMappings = {
    markAsSoldout: {
      newStatus: SOLDOUT,
      newApprovalStatus: APPROVAL,
      successMessage: '판매 완료로 전환 되었어요',
      shouldCloseModal: true,
    },
    markAsOnsale: {
      newStatus: ONSALE,
      newApprovalStatus: ONGOING,
      successMessage: '심사후 판매중으로 전환 됩니다',
      shouldCloseModal: false,
    },
  };

  const updateSellTractorStatus = (id: string, statusMappingKey: 'markAsSoldout' | 'markAsOnsale') => {
    if (!id) {
      return;
    }
    const { newStatus, newApprovalStatus, successMessage, shouldCloseModal } =
      sellTractorStatusMappings[statusMappingKey];

    patchUserSellTractor(
      {
        sellTractorId: id,
        reqBody: {
          status: newStatus,
          approvalStatus: newApprovalStatus,
        },
      },
      {
        onSuccess: () => {
          if (shouldCloseModal) {
            closeMarkAsSoldoutModal();
          }
          refetchUsersSellTractors();
          openNotification({ message: successMessage });
        },
      },
    );
  };

  const closeMarkAsSoldoutModal = () => {
    setTargetSellTractorId('');
    setIsMarkAsSoldoutModalVisible(false);
  };

  const handleMarkAsSoldout = (sellTractorId: string) => {
    updateSellTractorStatus(sellTractorId, 'markAsSoldout');
  };

  const handleMarkAsOnsale = (sellTractorId: string) => {
    updateSellTractorStatus(sellTractorId, 'markAsOnsale');
  };

  const handleShowRejectedReasonButtonClick = (product: IOpenMarketProduct) => {
    setIsRejectedReasonModalVisible(true);
    setRejectedReason(product.rejectedReason);
  };

  // TODO: 백-프론트 협의 후 이미지 데이터 형식 수정
  const mapImagesToTractorImageUrls = (images: IOpenMarketProductImage[]): tractorImageObject => {
    const imageUrls: tractorImageObject = {
      LEFT: '',
      FRONT: '',
      BACK: '',
      INNER: '',
      WORKINGMACHINE1: '',
      FRONTTIER: '',
      BACKTIER: '',
      LEFTTRACK: '',
      RIGHTTRACK: '',
      ETC1: '',
      ETC2: '',
      ETC3: '',
    };

    images.forEach((image) => {
      const { type, imageUrl } = image;

      if (imageTypes.includes(type as tractorImageType)) {
        imageUrls[type as tractorImageType] = imageUrl;
      }
    });

    return imageUrls;
  };

  const handleBumpButtonClick = useCallback(
    (product: IOpenMarketProduct, index: number) => {
      if (!product) {
        return;
      }

      if (usersSellTractors) {
        ampli.pvmClickBumpButtonInMyPage({
          product_id: product.id,
          index_in_list: index,
          total_on_sales_product_count:
            usersSellTractors.filter(
              (product: IOpenMarketProduct) => product.status === ONSALE && product.approvalStatus === APPROVAL,
            ).length || 0,
        });
      }

      setTargetSellTractor(product);
      setTargetSellTractorId(product.id);

      if (product.isNewProduct) {
        setIsNewProductCannotBumpModalVisible(true);
        return;
      }

      if (product.bump && product.bump.bumpTimes >= 5) {
        setIsBumpLimitPerTractorOverModalVisible(true);
        return;
      }

      if (
        product.bump &&
        product.bump?.lastBumpAt &&
        differenceInDays(new Date(), new Date(product.bump.lastBumpAt)) <= 6
      ) {
        setIsCanBumpAgainTimeModalVisible(true);
        return;
      }

      if (todaySellTractorBumpStatus?.todayBumpCount && todaySellTractorBumpStatus.todayBumpCount >= 1) {
        setIsBumpDailyLimitOverModalVisible(true);
        return;
      }
      setIsBumpModalVisible(true);
    },
    [todaySellTractorBumpStatus, usersSellTractors],
  );

  const bumpUserSellTractor = () => {
    postBumpUserSellTractor(
      {
        productId: targetSellTractorId,
        isAutoBumped: false,
      },
      {
        onSuccess: () => {
          openNotification({ message: '끌올이 완료 되었어요!' });
        },
        onError: (error) => {
          openNotification({ message: '끌어올리기 실패', type: 'error' });
        },
        onSettled: () => {
          closeBumpModal();
        },
      },
    );
  };

  const closeBumpModal = () => {
    setIsBumpModalVisible(false);
  };

  const handleEdit = (product: IOpenMarketProduct) => {
    ampli.pvmClickSellMachineEditButton({
      id: product.id,
      type: product.machine.machineType,
    });
    dispatch(
      userSellTractorActions.changeSellTractorSurveyForEdit({
        machineType: product.machine.machineType,
        sellTractorIdForEdit: product.id,
        brand: product.machine.brand.name,
        brandId: product.machine.brand.id,
        model: product.machine.model,
        sellTractorMachineInfo: {
          id: product.machine.id,
          approvalStatus: product.machine.approvalStatus,
          engineOutputInPs: product.machine.engineOutputInPs,
        },
        engineOutputInPs: product.machine.engineOutputInPs,
        rowConfig: product.machine.rowConfig,
        manufacturedYear: product.manufacturedYear,
        operationTime: product.operationTime,
        option: product.option,
        optionDetails: product.optionDetails,
        workingMachine: product.workingMachine,
        workingMachineDetails: product.workingMachineDetails,
        replacedParts: product.replacedParts,
        needRepairParts: product.needRepairParts,
        frontTireCondition: product.frontTireCondition,
        rearTireCondition: product.rearTireCondition,
        leftTrackCondition: product.leftTrackCondition,
        rightTrackCondition: product.rightTrackCondition,
        information: product.information,
        tractorImageUrls: mapImagesToTractorImageUrls(product.images),
        location: product.location,
        hopeSellingPrice: product.hopeSellingPrice / 10000,
        directImportYn: product.directImportYn,
        priceNegotiationYn: product.priceNegotiationYn,
      }),
    );
    setIsShowEditModal(true);
  };

  if (isGetUsersSellTractorsLoading) {
    return <AnimateLoading />;
  }

  return (
    <>
      <ProductSummaryCardGroup>
        {usersSellTractors && usersSellTractors.length > 0 ? (
          usersSellTractors.map((product: IOpenMarketProduct, index: number) => (
            <ProductSummaryCardWithActions key={product.id}>
              <UserSellProductDetailCard key={product.id} product={product as IUserSellProductForDetailCard} />
              <ButtonSection>
                {product.approvalStatus === APPROVAL && product.status === ONSALE && (
                  <>
                    <ButtonRow>
                      <GreyButton
                        onClick={() => {
                          handleBumpButtonClick(product, index);
                        }}
                      >
                        {'끌어올리기'}
                      </GreyButton>
                    </ButtonRow>
                    <ButtonRow>
                      <GreyButton onClick={() => handleEdit(product)}>{'수정하기'}</GreyButton>
                      <GreyButton
                        onClick={() => {
                          setTargetSellTractorId(product.id);
                          setIsMarkAsSoldoutModalVisible(true);
                        }}
                      >
                        {'판매 완료로 전환'}
                      </GreyButton>
                    </ButtonRow>
                  </>
                )}
                {product.approvalStatus === APPROVAL && product.status === SOLDOUT && (
                  <ButtonRow>
                    <GreyButton disabled={isPatchUserSellTractorLoading} onClick={() => handleMarkAsOnsale(product.id)}>
                      {'판매 중으로 전환'}
                    </GreyButton>
                  </ButtonRow>
                )}
                {product.approvalStatus === 'REJECT' && (
                  <ButtonRow>
                    <GreyButton onClick={() => handleShowRejectedReasonButtonClick(product)}>
                      {'판매 불가 사유 확인하기'}
                    </GreyButton>
                  </ButtonRow>
                )}
              </ButtonSection>
            </ProductSummaryCardWithActions>
          ))
        ) : (
          <EmptyState
            title={'아직 등록된 농기계가 없어요.'}
            buttonText={'농기계 등록하러 가기'}
            onClick={() => {
              ampli.pvmClickGoToSellMachineEntryPage({
                page_type: 'my_info_sell_machine_list_page',
                buttonType: type,
              });
              navigate(pageInfo.sellTractorEntryPage.path);
            }}
          />
        )}
      </ProductSummaryCardGroup>

      <PopupConfirmModal
        isShow={isShowEditModal}
        hasCloseButton={false}
        closeModal={() => setIsShowEditModal(false)}
        title={'수정 후 재심사 완료시\n내 매물이 상단에 등록! 🚀'}
        description={'재심사 완료시, 직거래 목록\n상단에 자동으로 재등록돼요!'}
        buttonArray={[
          {
            text: '확인',
            onClick: () => navigate(sellTractorPageInfo.overviewPage.path),
            color: 'blue',
          },
        ]}
      />

      <PopupConfirmModal
        isShow={isMarkAsSoldoutModalVisible && !!targetSellTractorId}
        hasCloseButton={false}
        title={'알려드립니다 💡'}
        description={'판매중으로 다시 전환 하시려면\n재심사 과정이 필요해요!'}
        buttonArray={[
          {
            text: '판매 완료 하기',
            onClick: () => handleMarkAsSoldout(targetSellTractorId),
            color: 'blue',
          },
          {
            text: '닫기',
            onClick: () => {
              closeMarkAsSoldoutModal();
            },
            color: 'gray',
          },
        ]}
      />

      <PopupConfirmModal
        isShow={isRejectedReasonModalVisible && !!rejectedReason}
        hasCloseButton={false}
        title={'하단의 사유를 확인 해주세요'}
        element={
          <ToastViewerBackground id={'rejected-reason'}>
            <ToastViewer content={rejectedReason!} />
          </ToastViewerBackground>
        }
        buttonArray={[
          {
            text: '확인',
            onClick: () => setIsRejectedReasonModalVisible(false),
            color: 'blue',
          },
        ]}
      />

      <PopupConfirmModal
        isShow={isBumpModalVisible}
        hasCloseButton={false}
        title={'🚀 주목받고 싶다면 지금 끌올!'}
        description={'끌올하면 상위에 재등록 돼요!\n지금 끌어올릴까요?\n(매물당 5회 가능)'}
        buttonArray={[
          {
            text: '끌어올리기',
            onClick: bumpUserSellTractor,
            color: 'blue',
          },
          {
            text: '닫기',
            onClick: closeBumpModal,
            color: 'gray',
          },
        ]}
      />

      <PopupConfirmModal
        isShow={isNewProductCannotBumpModalVisible}
        hasCloseButton={false}
        title={'😓 아직 끌올을 사용할 수 없어요'}
        description={`승인 후 일주일 뒤에 가능해요!\n${
          targetSellTractor?.isNewProduct
            ? format(addHours(addDays(new Date(targetSellTractor.approvalDate), 8), 1), 'M월 d일(EEEE) HH시', {
                locale: ko,
              })
            : ''
        }`}
        buttonArray={[
          {
            text: '확인',
            onClick: () => {
              setIsNewProductCannotBumpModalVisible(false);
              ampli.pvmClickBumpAlreadyBumpModalButton();
            },
            color: 'blue',
          },
        ]}
      />

      <PopupConfirmModal
        isShow={isBumpDailyLimitOverModalVisible}
        hasCloseButton={false}
        title={'☝🏻 끌올은 하루에 한대만!'}
        description={'할당된 횟수를 모두 사용 했어요.\n내일 다시 시도 해보세요!'}
        buttonArray={[
          {
            text: '확인',
            onClick: () => {
              setIsBumpDailyLimitOverModalVisible(false);
              ampli.pvmClickBumpOncePerDayModalButton();
              postMessageToRN({
                messageKey: 'flarelane_trackEvent',
                messageParam: flarelaneEventKeys.pvm_fail_bump,
              });
            },
            color: 'blue',
          },
        ]}
      />

      <PopupConfirmModal
        isShow={isBumpLimitPerTractorOverModalVisible}
        hasCloseButton={false}
        title={'🙌🏻 끌올을 5회 사용했어요!'}
        description={'이 매물에 사용할 수 있는\n끌올을 모두 사용하셨어요.\n(매물당 5회 가능)'}
        buttonArray={[
          {
            text: '확인',
            onClick: () => {
              setIsBumpLimitPerTractorOverModalVisible(false);
              ampli.pvmClickBumpUsedAllAllottedAtteptsModalButton();
            },
            color: 'blue',
          },
        ]}
      />

      <PopupConfirmModal
        isShow={isCanBumpAgainTimeModalVisible}
        hasCloseButton={false}
        title={'🙂 이미 끌올 했어요!'}
        description={`다음 끌어올리기는\n${
          targetSellTractor?.bump?.lastBumpAt
            ? format(addDays(new Date(targetSellTractor.bump?.lastBumpAt), 7), 'M월 d일(EEEE)', { locale: ko })
            : ''
        }에 가능해요!`}
        buttonArray={[
          {
            text: '확인',
            onClick: () => setIsCanBumpAgainTimeModalVisible(false),
            color: 'blue',
          },
        ]}
      />
    </>
  );
};

const ProductSummaryCardGroup = styled.div`
  display: flex;
  padding: var(--radius-large, 16px) var(--radius-large, 16px) 32px var(--radius-large, 16px);
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
`;

const ProductSummaryCardWithActions = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--radius-large, 16px);
`;

const ButtonSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const GreyButton = styled.button<{ isBlurred?: boolean }>`
  display: flex;
  max-height: 40px;
  padding: var(--radius-large, 16px);
  justify-content: center;
  align-items: center;
  gap: var(--radius-small, 4px);
  flex: 1 0 0;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);

  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};
  background: ${(props) =>
    props.isBlurred
      ? 'var(--Light-Scheme-Monochrome-grey20, #ececf3)'
      : 'var(--Light-Scheme-Monochrome-grey10, #f2f2f7)'};
  border: ${(props) => (props.isBlurred ? '1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3)' : 'none')};
`;

const ToastViewerBackground = styled.div`
  display: flex;
  padding: var(--radius-large, 16px);
  align-items: flex-start;
  gap: var(--radius-small, 4px);
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);

  max-height: 272px;

  overflow-y: scroll;
`;
